import {
  LightWeightOperator,
  LightweightRule,
  LightweightRuleEngine,
  LightweightRuleSet,
  RuleEngineComponentInput,
  RULESET_MIN_LENGTH,
  RuleValue,
  VALUELESS_OPERATORS
} from './types-constants';
import { FormControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { RuleEngineInput } from '@coin/shared/core/util-models';

export function emptyRule(): LightweightRule {
  return { field: '', operator: null, value: [] };
}

export function emptyRuleSet({ prefillRules }: { prefillRules?: boolean } = {}): LightweightRuleSet {
  const rules = prefillRules ? [emptyRule(), emptyRule()] : [];
  return { rules, condition: 'And' };
}

export function emptyRuleEngine(): LightweightRuleSet {
  return { condition: 'And', rules: [] };
}

export function isValuelessOperator(operator: LightWeightOperator): boolean {
  return VALUELESS_OPERATORS.has(operator);
}

export function ruleEngineValidator(config: { required?: boolean } = {}): ValidatorFn {
  const required = config.required ?? false;

  return (control: FormControl<LightweightRuleEngine>): ValidationErrors | null => {
    const validationError = { ruleEngine: 'general.rule-set-invalid' };

    if (!control.value?.rules?.length) return required ? validationError : null;

    if (control.value.rules.some(isRuleOrRuleSetInvalid)) return validationError;

    return null;
  };
}

export function isRuleOrRuleSetInvalid(ruleOrRuleSet: LightweightRule | LightweightRuleSet): boolean {
  function isRuleInvalid(rule: LightweightRule): boolean {
    return !rule.operator || !rule.field || isInvalidRuleValue(rule);
  }
  function isRuleSetInvalid(ruleSet: LightweightRuleSet): boolean {
    return ruleSet.rules.length < RULESET_MIN_LENGTH || ruleSet.rules.some(isRuleOrRuleSetInvalid);
  }

  return isRuleSet(ruleOrRuleSet) ? isRuleSetInvalid(ruleOrRuleSet) : isRuleInvalid(ruleOrRuleSet);
}

function isInvalidRuleValue({ value, operator }: LightweightRule): boolean {
  const isNullValue = !value?.length || value[0] === '' || value[0] === null || value[0] === undefined;
  return VALUELESS_OPERATORS.has(operator) ? !isNullValue : isNullValue;
}

export function defaultRuleValue(inputType: RuleEngineInput | RuleEngineComponentInput): RuleValue {
  if (typeof inputType === 'object') return inputType.getDefaultValue();
  if (inputType === 'System.Boolean') return [false];

  return emptyRule().value;
}

export function isRuleSet(ruleOrRuleSet: LightweightRule | LightweightRuleSet): ruleOrRuleSet is LightweightRuleSet {
  if ('rules' in ruleOrRuleSet && ruleOrRuleSet.rules.length) return true;

  const ruleExclusiveProperties: (keyof LightweightRule)[] = ['field', 'value', 'operator'];
  return ruleExclusiveProperties.every(field => !(field in ruleOrRuleSet));
}
